import {Box, Typography, useTheme} from "@mui/material";
import {ManagerStats} from "arteco-api-client-ts";
import React from "react";
import {Panel, SoftChip} from "../../../components/kit";


export interface EfficiencyIndicatorsProps {
	stats: ManagerStats;
}

export const EfficiencyIndicators = ({stats}: EfficiencyIndicatorsProps) => {
	interface Item {
		title: string;
		ratio: number;
		hours: number;
		diff?: number;
		description: string;
	}

	// Desvío de Presupuestación (#13747): de los proyectos y bolsas, consumido (Dedication, vida
	// entera, más lo provisional del mes) frente a lo planificado A LA MISMA FECHA. Un solo signo:
	// positivo es exceso, en % y en horas. Sin el «+1» de antes; cuotas y T&M no entran.
	const calcBudgetRatio = (stats: ManagerStats, ratio: boolean): number => {
		const medidos = stats.budgets?.filter(b => b.deviationHours != null) || [];
		const planificado = medidos.reduce((acc, b) => acc + (b.plannedHours || 0), 0);
		const desvio = medidos.reduce((acc, b) => acc + (b.deviationHours || 0), 0);
		if (ratio) {
			return planificado > 0 ? Math.round(desvio / planificado * 1000) / 10 : 0;
		}
		return desvio;
	}

	// Desvío de Ocupación: horas sin asignar sobre trabajadas, sin el «+1» (#13747). Con `mesesAtras`
	// mide un solo mes cerrado (1 = el último, 2 = el anterior), para comparar mes con mes y no
	// acumulados del año.
	const calcOccupancyRatio = (stats: ManagerStats, ratio: boolean, mesesAtras?: number): number => {

		const hoy = new Date();
		const objetivo = mesesAtras ? new Date(hoy.getFullYear(), hoy.getMonth() - mesesAtras, 1) : undefined;
		const selectedMonths = (objetivo
			? stats.months?.filter(m => m.year === objetivo.getFullYear() && m.month === objetivo.getMonth() + 1)
			: stats.months) || [];

		const totalWorked = selectedMonths.reduce((acc, act) => acc + (act.dedication?.workedHours || 0), 0) || 0;
		const totalUnassigned = selectedMonths.reduce((acc, act) => acc + (act.dedication?.unassignedHours || 0), 0) || 0;

		if (ratio) {
			return totalWorked > 0 ? Math.round(totalUnassigned / totalWorked * 1000) / 10 : 0;
		} else {
			return -totalUnassigned;
		}
	}

	// Variación en PUNTOS entre dos meses cerrados (#13747): antes era un cociente pintado con «%»
	// sin ×100 y solo visible si pasaba de 1, o sea casi nunca.
	const diff = (a: number, b: number): number | undefined => a - b;

	const efficiencies: Item[] = [
		{
			title: 'Desvío de Ocupación',
			ratio: calcOccupancyRatio(stats, true),
			hours: calcOccupancyRatio(stats, false),
			diff: diff(calcOccupancyRatio(stats, true, 1), calcOccupancyRatio(stats, true, 2)),
			description: "Horas sin asignar (sin presupuesto, ni T&M) sobre el total trabajado. La variación compara el último mes cerrado con el anterior."
		},
		{
			title: 'Desvío de Presupuestación',
			ratio: calcBudgetRatio(stats, true),
			hours: calcBudgetRatio(stats, false),
			diff: undefined,
			description: "Proyectos y bolsas: horas consumidas (cerradas y provisionales del mes) menos las planificadas a hoy. Positivo es exceso."
		}
	];

	return (
		<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "1fr 1fr"}, gap: 3}}>
			{efficiencies.map((item, idx) => (
				<EfficiencyCard key={idx} title={item.title}
								ratio={item.ratio}
								hours={item.hours}
								diff={item.diff}
								description={item.description}/>
			))}
		</Box>
	);
};

export interface EfficiencyCardProps {
	title: string;
	description: string;
	ratio: number;
	hours: number;
	diff?: number;
}

export const EfficiencyCard = ({title, description, ratio, hours, diff}: EfficiencyCardProps) => {
	const theme = useTheme();
	const tone = ratio <= 0 ? "success" : ratio >= 20 ? "error" : "warning";
	return (
		<Panel title={title} subtitle={description}>
			<Box sx={{display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap"}}>
				<Typography component="div" sx={{fontSize: "2.75rem", lineHeight: 1, fontWeight: 800, color: theme.palette[tone].main, fontVariantNumeric: "tabular-nums"}}>
					{ratio.toFixed(0)}%
				</Typography>
				<Typography variant="body2" color="text.secondary">{hours.toLocaleString("es-ES", {maximumFractionDigits: 1})} horas</Typography>
				{diff != undefined && diff !== 0 && (
					<SoftChip tone={diff > 0 ? "error" : "success"}
							  label={`${diff > 0 ? "+" : ""}${diff.toLocaleString("es-ES", {maximumFractionDigits: 1})} puntos frente al mes anterior`}/>
				)}
			</Box>
		</Panel>
	);
}
